import { ACCOUNT_TYPES } from "@tastyworks/shared-utils/misc/constants"
import type { TaxElection } from "./account-type"
import { ADDRESS_DESER, Address } from "./address"
import { RestResource } from "./common"
import { SchemaDeSerBuilder, type SchemaDeSer } from "./deser"
import {
  ENTITY_BENEFICIAL_OWNER_DESER,
  EntityBeneficialOwner,
} from "./entity-beneficial-owner"
import {
  ENTITY_SUITABILITY_DESER,
  EntitySuitability,
} from "./entity-suitability"
import type { DateISOString } from "./util/date-iso-string"

export enum ENTITY_RELATIONSHIP {
  EMPLOYEE = "Employee",
  MANAGER = "Managing Director/Officer",
  OWNER = "Owner",
}

export enum TRUST_REVOCABILITY {
  IRREVOCABLE = "IRREVOCABLE",
  REVOCABLE = "REVOCABLE",
}

export enum LEGAL_EXEMPTION_REASON {
  CFTC_REGISTERED_ENTITY = "CFTC Registered Entity",
  ISSUER_OF_SECURITIES = "Issuer of Securities",
  LISTED_ENTITY = "Listed Entity",
  PUBLIC_ACCOUNTING_FIRM = "Public Accounting Firm",
  REGISTERED_EXCHANGE_OR_CLEARING_AGENCY = "Registered Exchange or Clearing Agency",
  REGISTERED_INVESTMENT_ADVISER = "Registered Investment Adviser",
  REGISTERED_INVESTMENT_COMPANY = "Registered Investment Company",
  REGULATED_INSURANCE_COMPANY = "Regulated Insurance Company",
  USA_DEPARTMENT_OR_AGENCY = "USA Department or Agency",
  SEC_REGISTERED_ENTITY = "SEC Registered Entity",
}

export class Entity extends RestResource {
  address = new Address()
  entityType: ACCOUNT_TYPES = ACCOUNT_TYPES.BUSINESS
  legalName = ""
  entitySuitability?: EntitySuitability
  relationshipToEntity?: ENTITY_RELATIONSHIP
  businessNature = ""
  taxNumber = ""
  phoneNumber = ""
  secretaryName = ""
  hasForeignBankAffiliation?: boolean
  hasForeignInstitutionAffiliation?: boolean
  foreignInstitution?: string
  hasLegalExemption?: boolean
  legalExemptionReason?: LEGAL_EXEMPTION_REASON
  entityBeneficialOwners?: EntityBeneficialOwner[]
  taxElection?: TaxElection
  mailingAddress?: Address
  trustRevocability?: TRUST_REVOCABILITY
  grantorFirstName?: string
  grantorMiddleName?: string
  grantorLastName?: string
  grantorEmail?: string
  grantorTaxNumber?: string
  grantorBirthDate: DateISOString | null = null
  isHedgeFund?: boolean
}

export const ENTITY_DESER: SchemaDeSer<Entity> = new SchemaDeSerBuilder(Entity)
  .ofNested("address", ADDRESS_DESER, Address)
  .ofString("entityType")
  .ofString("id")
  .ofString("legalName")
  .ofNested("entitySuitability", ENTITY_SUITABILITY_DESER, EntitySuitability)
  .ofString("relationshipToEntity")
  .ofString("businessNature")
  .ofString("taxNumber")
  .ofString("phoneNumber")
  .ofString("secretaryName")
  .ofBoolean("hasForeignBankAffiliation")
  .ofBoolean("hasForeignInstitutionAffiliation")
  .ofString("foreignInstitution")
  .ofBoolean("hasLegalExemption")
  .ofString("legalExemptionReason")
  .ofArray(
    "entityBeneficialOwners",
    ENTITY_BENEFICIAL_OWNER_DESER,
    EntityBeneficialOwner
  )
  .ofString("taxElection")
  .ofNested("mailingAddress", ADDRESS_DESER, Address)
  .ofString("trustRevocability")
  .ofString("grantorFirstName")
  .ofString("grantorMiddleName")
  .ofString("grantorLastName")
  .ofString("grantorEmail")
  .ofString("grantorTaxNumber")
  .ofString("grantorBirthDate")
  .ofBoolean("isHedgeFund")
  .toDeSer()
